<template>
  <v-form lazy-validation ref="shiftReportForm" v-model="isShiftReportFormValid">
    <div v-if="isShiftSubmissionDisabled" class="expiry-container">
      <v-row no-gutters style="height: 200px;">
        <v-col class="d-flex justify-center align-center">
          <p>{{ $t("shiftReport.form.expired") }}</p>
        </v-col>
      </v-row>
    </div>
    <div class="mr-2" v-else>
      <v-row no-gutters class=" mt-6 multi-row">
        <h4 class="mb-4">{{ $t("shiftReport.form.shiftTargetQuantity") }}</h4>
        <v-row>
          <v-col>
            <wx-text-field
              v-model="currentShiftTargetQuantity"
              :rules="[() => validateCurrentQuantityCount()]"
              class="target-qty-input"
              outlined
              dense
              :disabled="isShiftSubmissionDisabled"
              min="0"
              max="99999"
              type="number"
              :placeholder="$t('shiftReport.form.addTargetQuantity')"
            />
          </v-col>
          <v-col>
            <wx-autocomplete
              v-model="selectedUnit"
              :items="selectableUnits"
              item-text="name"
              item-value="id"
              return-object
              class="target-qty-autocomplete"
              outlined
              dense
              disabled
            />
          </v-col>
        </v-row>
      </v-row>
      <!-- total pu produced quantity of that shift -->
      <v-row no-gutters class=" mt-6 multi-row">
        <p class="text-subtitle-2 font-weight-light mb-2">{{ $t("shiftReport.form.producedQuantity") }}</p>
        <p class="font-weight-bold mb-1">{{ currentShiftCount }}</p>
        <p class="text-sm-caption font-weight-light mb-1">{{ productionUnitBaseUnit }}</p>
      </v-row>
      <!-- total pu produced quantity of that shift previous shift -->
      <v-row no-gutters class=" mt-6 multi-row">
        <p class="text-subtitle-2 font-weight-light mb-2">{{ $t("shiftReport.form.previousShiftQuantity") }}</p>
        <p class="font-weight-bold mb-1">{{ previousShiftCount }}</p>
        <p class="text-sm-caption font-weight-light mb-1">{{ productionUnitBaseUnit }}</p>
      </v-row>
      <v-divider class="mt-4" />
      <!-- sentiment -->
      <v-row no-gutters class=" mt-2 multi-row">
        <p class="text-subtitle-2 font-weight-light mb-2">{{ $t("shiftReport.form.sentiment") }}</p>
        <v-btn-toggle v-model="sentiment" class="d-flex align-center" :disabled="isShiftSubmissionDisabled">
          <v-btn icon :value="'good'" class="no-background-hover">
            <v-icon class="sentiment-icon" :color="goodSentimentColor">
              mdi-emoticon-happy-outline
            </v-icon>
          </v-btn>
          <v-btn :value="'bad'" class="no-background-hover">
            <v-icon
              class="sentiment-icon "
              :class="{ 'selected-icon': sentiment === 'bad' }"
              :color="badSentimentColor"
            >
              mdi-emoticon-sad-outline
            </v-icon>
          </v-btn>
        </v-btn-toggle>
        <v-alert v-if="isSentimentErrorVisible" class="mt-4" dense outlined type="error">
          <p class="text-subtitle-2 mb-2">{{ $t("shiftReport.form.sentimentError") }}</p>
        </v-alert>
      </v-row>
      <!-- comments -->
      <v-row no-gutters class=" mt-12 multi-row">
        <h4 class="mb-4">{{ $t("shiftReport.form.commentsTitle") }}</h4>
        <wx-textarea
          :disabled="isShiftSubmissionDisabled"
          v-model="comments"
          class="comment-text"
          :rules="[validateCharactersLeftInComments]"
        />
      </v-row>
      <v-row no-gutters class=" mt-0">
        <p class="text-subtitle-2 font-weight-light mb-2 mt-3 translate-up">{{ afterCommentTextBoxContent }}</p>
      </v-row>
      <v-row no-gutters class=" mt-0">
        <p class="text-subtitle-2 font-weight-light mb-2">{{ formSubmissionCountdownLabel }}</p>
      </v-row>
      <v-divider class="mt-4" />
      <v-row no-gutters class="justify-end mt-4">
        <wx-btn-standard
          @click="clickSubmit"
          :disabled="isShiftSubmissionDisabled"
          :title="$t('common.save')"
          id="end-of-shift-report-save-btn"
          color="primary"
        >
          {{ $t("common.save") }}
        </wx-btn-standard>
      </v-row>
    </div>
  </v-form>
</template>
<script>
import TimeUtils from "@/components/TimeUtils";
import WxAutocomplete from "@/components/ui/WxAutocomplete.vue";
import WxTextField from "@/components/ui/WxTextField.vue";
import WxTextarea from "@/components/ui/WxTextarea.vue";
import WxBtnStandard from "@/components/ui/WxBtnStandard.vue";
import { mapGetters, mapActions } from "vuex";
import { getDateFromMillis } from "@/store/TimeUtils";
import {
  SHIFT_REPORT_GRACE_PERIOD_EXPIRED,
  SHIFT_REPORT_TO_CREATE,
  SHIFT_REPORT_TO_UPDATE,
} from "@/components/dashboard/shiftReports/ShiftReportService";
import { defineComponent } from "vue";
const MAX_ALLOWED_CHARACTERS = 10000;
function isValidDecimalGreaterThan(valueAsString, minValueExcluded) {
  let value = Number.parseFloat(valueAsString);
  if (isNaN(value)) {
    return false;
  } else {
    if (value <= minValueExcluded) {
      return false;
    }
  }
  return true;
}
export default defineComponent({
  components: { WxBtnStandard, WxTextarea, WxTextField, WxAutocomplete },
  data() {
    return {
      currentShiftTargetQuantity: null,
      sentiment: null,
      selectedUnit: null,
      comments: null,
      isShiftReportFormValid: true,
      msToExpiration: null,
      countDownTimer: null,
      submissionAttempted: false,
    };
  },
  props: {
    report: {
      type: Object,
      default: null,
    },
  },
  computed: {
    ...mapGetters("dashboard", ["activeProductionUnit"]),
    ...mapGetters("navigation", ["activeFactory"]),
    ...mapGetters("alerts", ["activeAlertInstances", "alerts", "activeAlertInstance"]),
    currentShiftCount() {
      return this.report?.currentShiftCount;
    },
    productionUnitBaseUnit() {
      if (this.selectableUnits && this.selectableUnits.length > 0 && this.report?.productionUnitBaseUnit) {
        return this.selectableUnits.find((unit) => unit.id === this.report?.productionUnitBaseUnit).name;
      } else {
        return "";
      }
    },
    isSentimentErrorVisible() {
      return !this.sentiment && this.submissionAttempted;
    },
    previousShiftCount() {
      return this.report?.previousShiftCount;
    },
    isShiftSubmissionDisabled() {
      return this.report?.type === SHIFT_REPORT_GRACE_PERIOD_EXPIRED;
    },
    workShiftName() {
      return this.report?.workShiftName;
    },
    isUnitSelectionDisabled() {
      return false;
    },
    formSubmissionCountdownLabel() {
      if (!this.report) {
        return "";
      }
      const type = this.report.type;
      if (type === SHIFT_REPORT_GRACE_PERIOD_EXPIRED) {
        return this.$t("shiftReport.form.expired");
      }
      if (type === SHIFT_REPORT_TO_UPDATE) {
        return "";
      } else {
        return this.$t("shiftReport.form.timeRemaining", { time: TimeUtils.durationTextHHMMSS(this.msToExpiration) });
      }
    },
    selectableUnits() {
      const unitKeys = {
        unit: "common.unitNames.unit",
        case: "common.unitNames.case",
        pack: "common.unitNames.pack",
        bottle: "common.unitNames.bottle",
        box: "common.unitNames.box",
        cycle: "common.unitNames.cycle",
        bag: "common.unitNames.bag",
        pallet: "common.unitNames.pallet",
        panel: "common.units.panel",
        part: "common.units.part",
        bend: "common.units.bend",
        can: "common.unitNames.can",
        tray: "common.unitNames.tray",
        kilogram: "tiles.quantity.totalWeight.units.kilograms",
        kg: "tiles.quantity.totalWeight.units.kilograms",
        g: "tiles.quantity.totalWeight.units.grams",
        mt: "tiles.quantity.totalWeight.units.tons",
        lb: "tiles.quantity.totalWeight.units.pounds",
        pound: "tiles.quantity.totalWeight.units.pounds",
        ounce: "tiles.quantity.totalWeight.units.ounces",
        oz: "tiles.quantity.totalWeight.units.ounces",
        ton: "tiles.quantity.totalWeight.units.tons",
        t: "tiles.quantity.totalWeight.units.imperialTons",
        liter: "tiles.quantity.totalWeight.units.liter",
        l: "tiles.quantity.totalVolume.units.liters",
        ml: "tiles.quantity.totalVolume.units.milliliters",
        hl: "tiles.quantity.totalVolume.units.hectoliters",
        m3: "tiles.quantity.totalVolume.units.cubicMeters",
        "fl-oz": "tiles.quantity.totalVolume.units.fluidOunces",
        gal: "tiles.quantity.totalVolume.units.gallons",
        "gal-uk": "tiles.quantity.totalVolume.units.imperialGallons",
        m: "tiles.quantity.totalLength.units.meters",
        meter: "tiles.quantity.totalLength.units.meters",
        mm: "tiles.quantity.totalLength.units.millimeters",
        km: "tiles.quantity.totalLength.units.kilometers",
        in: "tiles.quantity.totalLength.units.inches",
        ft: "tiles.quantity.totalLength.units.feet",
        feet: "tiles.quantity.totalLength.units.feet",
        mi: "tiles.quantity.totalLength.units.miles",
        yd: "tiles.quantity.totalLength.units.yards",
      };
      return Object.entries(unitKeys).map(([id, translationKey]) => ({
        id,
        // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
        name: this.$t(translationKey),
      }));
    },
    badSentimentColor() {
      return this.sentiment === "bad" ? "success" : "";
    },
    goodSentimentColor() {
      return this.sentiment === "good" ? "success" : "";
    },
    afterCommentTextBoxContent() {
      let creationTime = "";
      let userDisplayName = "";
      if (this.report) {
        if (this.report.creationTime) {
          creationTime = getDateFromMillis(this.report?.creationTime, this.activeFactory.timezone);
        }
        if (this.report.userDisplayName) {
          userDisplayName = this.report.userDisplayName;
        }
      }
      return `${userDisplayName} ${creationTime}`;
    },
  },
  watch: {
    report() {
      if (this.report) {
        this.submissionAttempted = false;
        this.sentiment = this.report.sentiment;
        this.selectedUnit = this.findSelectableUnit();
        this.currentShiftTargetQuantity = this.report.shiftTargetQty;
        this.comments = this.report.comments;
        this.msToExpiration = null;
        if (this.countDownTimer) {
          clearInterval(this.countDownTimer);
        }
        if (this.report.type === SHIFT_REPORT_TO_CREATE) {
          this.msToExpiration = this.report.timeLeftMs;
          this.countDownTimer = setInterval(() => {
            if (this.msToExpiration > 0) {
              this.msToExpiration -= 1000;
            } else {
              clearInterval(this.countDownTimer);
              this.msToExpiration = 0;
            }
          }, 1000);
        }

        this.$refs.shiftReportForm.resetValidation();
      }
    },
  },
  beforeDestroy() {
    clearInterval(this.countDownTimer);
  },
  methods: {
    ...mapActions("shiftReports", ["update", "create"]),
    ...mapActions("alerts", ["completeAlertInstance"]),
    validateCharactersLeftInComments() {
      if (this.comments) {
        if (this.comments.length > MAX_ALLOWED_CHARACTERS) {
          return this.$t("shiftReport.form.maxCommentCharacters", { maxCommentCharacters: MAX_ALLOWED_CHARACTERS });
        }
      } else if (!this.comments || this.comments.trim().length === 0) {
        return this.$t("shiftReport.form.commentsAreRequired");
      }
      return true;
    },
    findAssociatedInstances() {
      if (this.activeAlertInstances && this.alerts && this.report) {
        // eslint-disable-next-line no-unused-vars
        const flattenedAlertInstances = Array.from(this.activeAlertInstances, ([key, items]) => items)
          ?.flat()
          ?.filter(
            (instance) =>
              instance.productionUnitId === this.activeProductionUnit.id &&
              instance.raisedTime >= this.report.workShiftStartTime &&
              instance.raisedTime <= this.report.workShiftEndTime,
          );
        const filteredAlerts = this.alerts.filter(
          (alert) =>
            (alert.type === "EndOfShiftReport" || alert.type === "EndOfShift") &&
            alert.work_shift_ids.filter((wsid) => wsid === this.report.workShiftId),
        );
        return flattenedAlertInstances.filter((instance) =>
          filteredAlerts.filter((alert) => instance.alertId === alert.id),
        );
      }
    },
    findSelectableUnit() {
      return this.selectableUnits.find((unit) => unit.id === this.report?.productionUnitBaseUnit);
    },
    clickSubmit() {
      if (!this.submissionAttempted) {
        this.submissionAttempted = true;
      }
      if (this.isSentimentErrorVisible) {
        return;
      }
      if (!this.isShiftSubmissionDisabled) {
        const validForm = this.$refs.shiftReportForm.validate();
        if (validForm) {
          const request = {
            payload: this.createServiceObject(),
            productionUnitId: this.activeProductionUnit.id,
            id: this.report.type === SHIFT_REPORT_TO_UPDATE ? this.report.id : undefined,
          };
          (this.report.type === SHIFT_REPORT_TO_CREATE ? this.create : this.update)({ request });
        }
      }
    },
    createServiceObject() {
      return {
        work_shift_id: this.report.workShiftId,
        work_shift_start_time: this.report.workShiftStartTime,
        shift_target_qty: this.currentShiftTargetQuantity,
        shift_target_unit: this.getTargetUnit(),
        comments: this.comments,
        sentiment: this.sentiment,
      };
    },
    getTargetUnit() {
      const { id } = this.selectedUnit;
      const map = {
        pound: "lb",
        kilogram: "kg",
        ton: "t",
        liter: "l",
        feet: "ft",
        meter: "m",
      };
      return map[id] ?? id;
    },
    validateCurrentQuantityCount() {
      const targetQty = this.convertToDottedDecimal(this.currentShiftTargetQuantity);
      if (isNaN(targetQty) || !isValidDecimalGreaterThan(targetQty, 0.0)) {
        return this.$t("dashboard.productionRun.errors.invalidPlannedQuantity");
      }
      return true;
    },
    convertToDottedDecimal(value) {
      if (value) {
        let valueAsString = String(value);
        if (valueAsString.indexOf(",") >= 0) {
          return Number(valueAsString.replace(",", "."));
        } else {
          return Number(valueAsString);
        }
      }
      return null;
    },
  },
});
</script>

<style scoped lang="scss">
::v-deep(.target-qty-input input::placeholder) {
  font-size: 0.84rem !important;
}
::v-deep(.target-qty-autocomplete .v-label) {
  font-size: 0.55rem !important;
}
::v-deep(.comment-text textarea::placeholder) {
  font-size: 0.25rem !important;
}
.multi-row {
  flex-direction: column;
}

.no-background-hover {
  background-color: transparent !important;
  box-shadow: none !important;
  border: 0 none transparent !important;
  margin-right: 12px;
  transition: color 0.2s ease;
  color: transparent !important;
}

.no-background-hover.selected {
  background-color: transparent !important;
}

.sentiment-icon {
  font-size: 36px !important;
}

.shift-report-form-footer {
  justify-content: flex-end;
  column-gap: var(--btn-inline-margin);
  padding-bottom: var(--grid-gutter);
}
.translate-up {
  // According to the designs this text fields needs to hug the bottom part of the text area. vue-adds an invisible element that
  // adds extra padding, breaking our design.
  transform: translateY(-20px) !important;
}
.expiry-container {
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
